/**
 * Per-Dish Calculation Utilities
 * Functions to calculate consumption per dish by combining calendarized consumption data with sales data
 */

import type {
  CalendarizedCO2eMonthData,
  CalendarizedMonthData,
  CoversVolume,
  PaginatedResponse,
} from '../types/api';
import { convertWaterToLiters, WATER_UNIT } from './formatters';

// ============================================================================
// Types
// ============================================================================

export interface PerDishMetrics {
  kwh_per_dish?: number | null;
  m3_water_per_dish?: number | null;
  m3_gas_per_dish?: number | null;
  kg_recyclables_per_dish?: number | null;
  kg_organic_waste_per_dish?: number | null;
  kg_general_waste_per_dish?: number | null;
  kg_co2e_per_dish?: number | null;
  electricity_cost_per_dish?: number | null;
  water_cost_per_dish?: number | null;
  gas_cost_per_dish?: number | null;
  // Also include total values for toggle functionality
  kwh_total?: number | null;
  m3_water_total?: number | null;
  m3_gas_total?: number | null;
  kg_recyclables_total?: number | null;
  kg_organic_waste_total?: number | null;
  kg_general_waste_total?: number | null;
  kg_co2e_total?: number | null;
  electricity_cost_total?: number | null;
  water_cost_total?: number | null;
  gas_cost_total?: number | null;
  // Metadata
  covers_count?: number | null;
  has_sales_data: boolean;
  month_name: string;
  year: number;
  month: number;
}

export type DisplayMode = 'per_dish' | 'total';
export type UtilityCostService = 'electricity' | 'water' | 'gas';

export interface ChartDataPoint {
  date: string;
  value: number | null;
  formatted_date: string;
  // Additional metadata for tooltips
  total_value?: number;
  per_dish_value?: number;
  covers_count?: number;
  has_sales_data?: boolean;
}

export function calculateUtilityCostPerDishSummary(
  data: readonly PerDishMetrics[],
): Record<UtilityCostService, number | null> {
  const calculate = (
    totalKey: 'electricity_cost_total' | 'water_cost_total' | 'gas_cost_total',
  ): number | null => {
    let totalCost = 0;
    let totalCovers = 0;

    data.forEach((month) => {
      const cost = month[totalKey];
      const covers = month.covers_count;
      if (
        typeof cost === 'number' &&
        typeof covers === 'number' &&
        covers > 0
      ) {
        totalCost += cost;
        totalCovers += covers;
      }
    });

    return totalCovers > 0 ? totalCost / totalCovers : null;
  };

  return {
    electricity: calculate('electricity_cost_total'),
    water: calculate('water_cost_total'),
    gas: calculate('gas_cost_total'),
  };
}

// ============================================================================
// Date Utilities
// ============================================================================

/**
 * Convert year/month to YYYY-MM string format
 */
export function formatMonthString(year: number, month: number): string {
  const monthStr = String(month).padStart(2, '0');
  return `${year}-${monthStr}`;
}

/**
 * Parse YYYY-MM string to year/month numbers
 */
export function parseMonthString(monthStr: string): {
  year: number;
  month: number;
} {
  const parts = monthStr.split('-');
  const year = Number(parts[0]);
  const month = Number(parts[1]);

  if (isNaN(year) || isNaN(month)) {
    throw new Error(
      `Invalid month string format: ${monthStr}. Expected YYYY-MM format.`,
    );
  }

  return { year, month };
}

/**
 * Check if a date range overlaps with a specific month
 */
export function periodOverlapsMonth(
  periodStart: string,
  periodEnd: string,
  year: number,
  month: number,
): boolean {
  const monthStart = new Date(year, month - 1, 1);
  const monthEnd = new Date(year, month, 0); // Last day of month

  const startDate = new Date(periodStart);
  const endDate = new Date(periodEnd);

  // Check if periods overlap: period_start <= month_end AND period_end >= month_start
  return startDate <= monthEnd && endDate >= monthStart;
}

// ============================================================================
// Sales Data Utilities
// ============================================================================

/**
 * Find covers data for a specific month
 * Handles cases where sales periods might span multiple months or partially overlap
 */
export function findCoversForMonth(consumptionData: CalendarizedMonthData): {
  covers_count: number;
  has_data: boolean;
};
export function findCoversForMonth(
  coversVolumes: CoversVolume[],
  year: number,
  month: number,
): { covers_count: number; has_data: boolean };
export function findCoversForMonth(
  coversVolumes: PaginatedResponse<CoversVolume>,
  year: number,
  month: number,
): { covers_count: number; has_data: boolean };
export function findCoversForMonth(
  arg1:
    | CalendarizedMonthData
    | CoversVolume[]
    | PaginatedResponse<CoversVolume>,
  arg2?: number,
  arg3?: number,
): { covers_count: number; has_data: boolean } {
  if (Array.isArray(arg1) || 'items' in arg1) {
    const coversArray = Array.isArray(arg1) ? arg1 : arg1.items;
    const year = arg2;
    const month = arg3;

    if (typeof year !== 'number' || typeof month !== 'number') {
      return { covers_count: 0, has_data: false };
    }

    const overlappingPeriods = coversArray.filter((volume) =>
      periodOverlapsMonth(volume.period_start, volume.period_end, year, month),
    );

    if (overlappingPeriods.length === 0) {
      return { covers_count: 0, has_data: false };
    }

    const totalCovers = overlappingPeriods.reduce(
      (sum, volume) => sum + volume.covers_count,
      0,
    );

    return { covers_count: totalCovers, has_data: true };
  }

  const coversCount = arg1.consumption.covers_count;

  if (typeof coversCount !== 'number' || coversCount <= 0) {
    return { covers_count: 0, has_data: false };
  }

  return { covers_count: coversCount, has_data: true };
}

// ============================================================================
// Per-Dish Calculation Functions
// ============================================================================

/**
 * Calculate per-dish metrics for a single month
 */
export function calculatePerDishForMonth(
  consumptionData: CalendarizedMonthData,
  coversData: { covers_count: number; has_data: boolean },
  co2eData?: CalendarizedCO2eMonthData,
): PerDishMetrics {
  const { consumption, year, month, month_name } = consumptionData;
  const { covers_count, has_data } = coversData;

  // Base result with totals always available
  const result: PerDishMetrics = {
    // Total values (always available)
    kwh_total: consumption.kwh ?? null,
    m3_water_total: convertWaterToLiters(consumption.m3_water),
    m3_gas_total: consumption.m3_gas ?? null,
    kg_recyclables_total: consumption.kg_recyclables ?? null,
    kg_organic_waste_total: consumption.kg_organic_waste ?? null,
    kg_general_waste_total: consumption.kg_general_waste ?? null,
    kg_co2e_total: co2eData?.co2e.kg_co2e ?? null,
    electricity_cost_total: consumption.electricity_cost ?? null,
    water_cost_total: consumption.water_cost ?? null,
    gas_cost_total: consumption.gas_cost ?? null,

    // Per-dish values (calculated if sales data available)
    kwh_per_dish: null,
    m3_water_per_dish: null,
    m3_gas_per_dish: null,
    kg_recyclables_per_dish: null,
    kg_organic_waste_per_dish: null,
    kg_general_waste_per_dish: null,
    kg_co2e_per_dish: null,
    electricity_cost_per_dish: null,
    water_cost_per_dish: null,
    gas_cost_per_dish: null,

    // Metadata
    covers_count: has_data ? covers_count : null,
    has_sales_data: has_data,
    month_name,
    year,
    month,
  };

  // Only calculate per-dish metrics if we have sales data and covers > 0
  if (has_data && covers_count > 0) {
    result.kwh_per_dish = consumption.kwh
      ? consumption.kwh / covers_count
      : null;
    result.m3_water_per_dish = consumption.m3_water
      ? convertWaterToLiters(consumption.m3_water / covers_count)
      : null;
    result.m3_gas_per_dish = consumption.m3_gas
      ? consumption.m3_gas / covers_count
      : null;
    result.kg_recyclables_per_dish = consumption.kg_recyclables
      ? consumption.kg_recyclables / covers_count
      : null;
    result.kg_organic_waste_per_dish = consumption.kg_organic_waste
      ? consumption.kg_organic_waste / covers_count
      : null;
    result.kg_general_waste_per_dish = consumption.kg_general_waste
      ? consumption.kg_general_waste / covers_count
      : null;
    result.kg_co2e_per_dish = co2eData?.co2e.kg_co2e
      ? co2eData.co2e.kg_co2e / covers_count
      : null;
    result.electricity_cost_per_dish =
      consumption.electricity_cost !== null &&
      consumption.electricity_cost !== undefined
        ? consumption.electricity_cost / covers_count
        : null;
    result.water_cost_per_dish =
      consumption.water_cost !== null && consumption.water_cost !== undefined
        ? consumption.water_cost / covers_count
        : null;
    result.gas_cost_per_dish =
      consumption.gas_cost !== null && consumption.gas_cost !== undefined
        ? consumption.gas_cost / covers_count
        : null;
  }

  return result;
}

/**
 * Calculate per-dish metrics for multiple months
 */
export function calculatePerDishMetrics(
  consumptionData: CalendarizedMonthData[],
  co2eData: CalendarizedCO2eMonthData[] | undefined,
  coversVolumes: CoversVolume[] | PaginatedResponse<CoversVolume>,
): PerDishMetrics[] {
  return consumptionData.map((monthData) => {
    const calendarizedCoversCount = monthData.consumption.covers_count ?? null;

    const coversData =
      calendarizedCoversCount && calendarizedCoversCount > 0
        ? { covers_count: calendarizedCoversCount, has_data: true }
        : findCoversForMonth(
            Array.isArray(coversVolumes) ? coversVolumes : coversVolumes.items,
            monthData.year,
            monthData.month,
          );
    const co2eMonthData = co2eData?.find(
      (d) => d.year === monthData.year && d.month === monthData.month,
    );

    return calculatePerDishForMonth(monthData, coversData, co2eMonthData);
  });
}

// ============================================================================
// Chart Data Transformation
// ============================================================================

/**
 * Transform per-dish metrics to chart data points for a specific consumption type
 */
export function transformToChartData(
  perDishMetrics: PerDishMetrics[],
  consumptionType: keyof Pick<
    PerDishMetrics,
    | 'kwh_per_dish'
    | 'm3_water_per_dish'
    | 'm3_gas_per_dish'
    | 'kg_recyclables_per_dish'
    | 'kg_organic_waste_per_dish'
    | 'kg_general_waste_per_dish'
    | 'kg_co2e_per_dish'
  >,
  totalType: keyof Pick<
    PerDishMetrics,
    | 'kwh_total'
    | 'm3_water_total'
    | 'm3_gas_total'
    | 'kg_recyclables_total'
    | 'kg_organic_waste_total'
    | 'kg_general_waste_total'
    | 'kg_co2e_total'
  >,
  displayMode: DisplayMode = 'per_dish',
): ChartDataPoint[] {
  return perDishMetrics
    .map((metrics) => {
      const perDishValue = metrics[consumptionType];
      const totalValue = metrics[totalType];

      let value: number;
      let hasValidData = false;

      // Determine which value to use based on display mode and data availability
      if (displayMode === 'per_dish') {
        if (perDishValue !== null && perDishValue !== undefined) {
          value = perDishValue;
          hasValidData = true;
        } else if (
          totalValue !== null &&
          totalValue !== undefined &&
          !metrics.has_sales_data
        ) {
          // Fall back to total if no sales data (as per user requirement)
          value = totalValue;
          hasValidData = true;
        } else {
          return null; // Skip this data point
        }
      } else if (displayMode === 'total') {
        if (totalValue !== null && totalValue !== undefined) {
          value = totalValue;
          hasValidData = true;
        } else {
          return null; // Skip this data point
        }
      } else {
        // This should never happen with the current DisplayMode type
        return null;
      }

      if (!hasValidData) {
        return null;
      }

      const dateStr = formatMonthString(metrics.year, metrics.month);

      return {
        date: dateStr,
        value: Math.round(value * 10000) / 10000, // Round to 4 decimal places
        formatted_date: metrics.month_name,
        // Additional metadata for tooltips and 'both' mode
        total_value: totalValue,
        per_dish_value: perDishValue,
        covers_count: metrics.covers_count,
        has_sales_data: metrics.has_sales_data,
      };
    })
    .filter((point) => point !== null) as ChartDataPoint[];
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Get the appropriate unit string based on consumption type and display mode
 */
export function getUnit(
  consumptionType: string,
  displayMode: DisplayMode,
): string {
  const baseUnits: Record<string, string> = {
    kwh: 'kWh',
    m3_water: WATER_UNIT.TOTAL,
    m3_gas: 'm³',
    kg_recyclables: 'kg',
    kg_organic_waste: 'kg',
    kg_general_waste: 'kg',
    kg_co2e: 'kgCO₂e',
  };

  const perDishUnits: Record<string, string> = {
    kwh: 'kWh/plato',
    m3_water: WATER_UNIT.PER_DISH,
    m3_gas: 'm³/plato',
    kg_recyclables: 'kg/plato',
    kg_organic_waste: 'kg/plato',
    kg_general_waste: 'kg/plato',
    kg_co2e: 'kgCO₂e/plato',
  };

  const key = consumptionType.replace('_per_dish', '').replace('_total', '');

  if (displayMode === 'per_dish') {
    return perDishUnits[key] || 'unidad/plato';
  } else if (displayMode === 'total') {
    return baseUnits[key] || 'unidad';
  } else {
    return baseUnits[key] || 'unidad'; // fallback for type safety
  }
}

/**
 * Generate date range in YYYY-MM format from filters
 */
export function generateDateRangeFromFilters(filters: {
  start_date?: string;
  end_date?: string;
}): { start_month: string; end_month: string } | null {
  if (!filters.start_date || !filters.end_date) {
    return null;
  }

  const start_month = filters.start_date.slice(0, 7);
  const end_month = filters.end_date.slice(0, 7);
  const monthPattern = /^\d{4}-(0[1-9]|1[0-2])$/;
  if (!monthPattern.test(start_month) || !monthPattern.test(end_month)) {
    return null;
  }

  return { start_month, end_month };
}

// ============================================================================
// Chart Height Calculation Utilities
// ============================================================================

/**
 * Screen size breakpoints for responsive chart sizing
 */
export enum ScreenSize {
  MOBILE = 'mobile',
  TABLET = 'tablet',
  DESKTOP = 'desktop',
}

/**
 * Get current screen size based on window width
 */
export function getScreenSize(): ScreenSize {
  if (typeof window === 'undefined') {
    return ScreenSize.DESKTOP; // Default for SSR
  }

  const width = window.innerWidth;
  if (width < 768) {
    return ScreenSize.MOBILE;
  } else if (width < 1200) {
    return ScreenSize.TABLET;
  }
  return ScreenSize.DESKTOP;
}

/**
 * Calculate optimal chart height based on data points and screen size
 * Provides smooth scaling that adapts to data density and viewport with good aspect ratios
 */
const chartHeightConfig = {
  [ScreenSize.MOBILE]: {
    base: 280,
  },
  [ScreenSize.TABLET]: {
    base: 320,
  },
  [ScreenSize.DESKTOP]: {
    base: 350,
  },
} as const;

export function calculateDynamicChartHeight(
  _dataPoints: number,
  screenSize: ScreenSize = getScreenSize(),
): number {
  const config = chartHeightConfig[screenSize];
  return config?.base ?? 360;
}

interface ChartHeightTargetOptions {
  dataPointCount: number;
  screenSize?: ScreenSize;
}

export function calculateChartHeightTarget({
  dataPointCount,
  screenSize = getScreenSize(),
}: ChartHeightTargetOptions): number {
  return calculateDynamicChartHeight(dataPointCount, screenSize);
}

export function easeChartHeight(
  previousHeight: number | undefined,
  targetHeight: number,
  smoothing = 0.25,
): number {
  if (!Number.isFinite(previousHeight ?? Number.NaN)) {
    return targetHeight;
  }

  if (smoothing <= 0 || smoothing >= 1) {
    return targetHeight;
  }

  const current = previousHeight ?? targetHeight;
  return current + (targetHeight - current) * smoothing;
}

/**
 * Calculate optimal chart height for PerDishConsumptionChart
 * Takes chart data and display mode into account
 */
export function calculateChartHeight(chartData: ChartDataPoint[]): number {
  const dataPoints = chartData.length;

  // Empty charts get a minimum height
  if (dataPoints === 0) {
    return chartHeightConfig[ScreenSize.DESKTOP].base;
  }

  return calculateChartHeightTarget({ dataPointCount: dataPoints });
}
